import api from "@/lib/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });

    // Force an immediate update check — helps kick out stale SWs on Chrome.
    try { reg.update(); } catch { /* ignore */ }

    // When a new SW is installed, tell it to skipWaiting so it activates right away.
    if (reg.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    reg.addEventListener("updatefound", () => {
      const newWorker = reg.installing;
      if (!newWorker) return;
      newWorker.addEventListener("statechange", () => {
        if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
          // New SW installed alongside an existing controller → ask it to take over
          newWorker.postMessage({ type: "SKIP_WAITING" });
        }
      });
    });

    // When the active SW changes, reload the page once so fresh JS is used.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });

    return reg;
  } catch (e) {
    console.warn("SW registration failed:", e);
    return null;
  }
}

export async function subscribeToPush() {
  if (!("PushManager" in window) || !VAPID_PUBLIC_KEY) return null;

  const reg = await registerServiceWorker();
  if (!reg) return null;

  // Wait for SW to be ready
  const swReg = await navigator.serviceWorker.ready;

  // Check existing subscription
  let subscription = await swReg.pushManager.getSubscription();

  if (!subscription) {
    try {
      subscription = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    } catch (e) {
      console.warn("Push subscription failed:", e);
      return null;
    }
  }

  // Send subscription to backend
  try {
    await api.post("/push/subscribe", {
      subscription_info: subscription.toJSON(),
    });
  } catch (e) {
    console.warn("Failed to register subscription on server:", e);
  }

  return subscription;
}

export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) return;

  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  if (subscription) {
    await subscription.unsubscribe();
  }

  try {
    await api.delete("/push/unsubscribe");
  } catch (e) {
    // Non-critical
  }
}

export async function isPushSubscribed() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

export async function testPushNotification() {
  try {
    const res = await api.post("/push/test");
    return res.data;
  } catch (e) {
    throw e;
  }
}
